import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { Modal } from '../Modal/Modal'
import { createColumnHelper, ColumnDef } from '@tanstack/react-table'
import { DataTable } from '../../DataTable'
import { DataTableProvider } from '../../DataTable'
import { ColumnsStoreProvider } from '../../../stores/ColumnsStore/ColumnsStoreProvider'
import { dataTableSortingStoreRepo } from '../../DataTable/DataTableSorting/DataTableSortingStore'
import { useProductVariantSearch } from './useProductVariantSearch'
import {
  ProductSearchModalProps,
  ProductVariantSelection,
  SelectedVariant,
  ProductWithSelectedVariants,
} from './types'
import { Product, VariantCatalog } from '../../../services/products_api'
import { OverflownTextTooltip } from '../../OverflownTextTooltip'
import { TriangleAlert } from 'lucide-react'
import Select from '../../FormUtils/Select'
import { useForm } from 'react-hook-form'

// Column helpers for each data type
const productColumnHelper = createColumnHelper<Product>()
const variantColumnHelper = createColumnHelper<VariantCatalog>()

/**
 * Reusable ProductSearchModal component for selecting products and/or variants
 *
 * Supports three modes:
 * - 'product-only': Select products without variants
 * - 'product-with-variants': Select products with their variants
 * - 'variants-only': Select only variants
 *
 * @example
 * // Samples feature - single variant selection
 * <ProductSearchModal
 *   show={show}
 *   onHide={onHide}
 *   selectionMode="variants-only"
 *   maxSelections={1}
 *   onSelect={(selection) => {
 *     const variant = selection.variants?.[0]
 *     if (variant) addVariantToRequest(variant)
 *   }}
 * />
 *
 * @example
 * // Rebates feature - multiple products with variants
 * <ProductSearchModal
 *   show={show}
 *   onHide={onHide}
 *   selectionMode="product-with-variants"
 *   maxSelections={10}
 *   onSelect={(selection) => {
 *     selection.productsWithVariants?.forEach(({ product, variants }) => {
 *       addProductWithVariants(product, variants)
 *     })
 *   }}
 * />
 */
export function ProductSearchModal({
  show,
  onHide,
  selectionMode,
  onSelect,
  maxSelections = 10,
  multiSelect = false,
  initialSelection,
  title = 'Select Products',
  selectButtonText = 'Add Selected',
  showProductFilter = false,
  restrictToProduct,
}: ProductSearchModalProps) {
  // Determine which data to fetch based on selection mode
  const fetchMode =
    selectionMode === 'product-only'
      ? 'products'
      : selectionMode === 'variants-only'
        ? 'variants'
        : 'both'

  // Use the custom search hook
  const search = useProductVariantSearch({
    mode: fetchMode,
    initialFilters: restrictToProduct
      ? { productId: restrictToProduct }
      : undefined,
    pageSize: 50,
    enabled: show, // Only fetch when modal is open
  })

  // Form control for product filter dropdown
  const { control, watch, setValue } = useForm<{ productFilter: string }>({
    defaultValues: {
      productFilter: restrictToProduct?.toString() || '',
    },
  })

  // Watch the product filter value
  const productFilterValue = watch('productFilter')

  // Selection state - supports both single and multi-select
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [selectedVariant, setSelectedVariant] = useState<VariantCatalog | null>(
    null
  )
  // Multi-select state
  const [selectedProducts, setSelectedProducts] = useState<Set<number>>(
    new Set()
  )
  const [selectedVariants, setSelectedVariants] = useState<Set<number>>(
    new Set()
  )

  // Initialize selection from props
  useEffect(() => {
    if (!show || !initialSelection) return

    // For multi-select mode, initialize Set of selected IDs
    if (multiSelect) {
      if (initialSelection.products && initialSelection.products.length > 0) {
        const productIds = new Set(initialSelection.products.map((p) => p.id))
        setSelectedProducts(productIds)
      }

      if (initialSelection.variants && initialSelection.variants.length > 0) {
        const variantIds = new Set(initialSelection.variants.map((v) => v.id))
        setSelectedVariants(variantIds)
      }
    } else {
      // For single-select mode, set the single selection
      if (initialSelection.products && initialSelection.products.length > 0) {
        const product = search.products.find(
          (p) => p.id === initialSelection.products![0].id
        )
        if (product) setSelectedProduct(product)
      }

      if (initialSelection.variants && initialSelection.variants.length > 0) {
        const variant = search.variants.find(
          (v) => v.id === initialSelection.variants![0].id
        )
        if (variant) setSelectedVariant(variant)
      }
    }
  }, [show, initialSelection, multiSelect])

  // Sync product filter value with search filters
  useEffect(() => {
    const productId = productFilterValue ? Number(productFilterValue) : null
    search.setFilters({ productId: productId || undefined })
  }, [productFilterValue])

  // Update filters when restrictToProduct changes (when modal opens)
  useEffect(() => {
    if (show && restrictToProduct) {
      setValue('productFilter', restrictToProduct.toString())
      search.setFilters({ productId: restrictToProduct })
    }
  }, [show, restrictToProduct])

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setSelectedProduct(null)
      setSelectedVariant(null)
      setSelectedProducts(new Set())
      setSelectedVariants(new Set())
      setValue('productFilter', restrictToProduct?.toString() || '')
      search.reset()
      dataTableSortingStoreRepo.getStore(tableKey).getState().setSorting([])
    }
  }, [show, restrictToProduct])

  // Check if something is selected
  const hasSelection = multiSelect
    ? selectionMode === 'product-only'
      ? selectedProducts.size > 0
      : selectedVariants.size > 0
    : selectionMode === 'product-only'
      ? !!selectedProduct
      : !!selectedVariant

  // Handle row click
  const handleRowClick = useCallback(
    (row: Product | VariantCatalog) => {
      if (multiSelect) {
        // Multi-select mode: toggle selection
        if (selectionMode === 'product-only') {
          const product = row as Product
          setSelectedProducts((prev) => {
            const next = new Set(prev)
            if (next.has(product.id)) {
              next.delete(product.id)
            } else if (next.size < maxSelections) {
              next.add(product.id)
            }
            return next
          })
        } else {
          const variant = row as VariantCatalog
          setSelectedVariants((prev) => {
            const next = new Set(prev)
            if (next.has(variant.id)) {
              next.delete(variant.id)
            } else if (next.size < maxSelections) {
              next.add(variant.id)
            }
            return next
          })
        }
      } else {
        // Single-select mode: replace selection
        if (selectionMode === 'product-only') {
          setSelectedProduct(row as Product)
          setSelectedVariant(null)
        } else {
          setSelectedVariant(row as VariantCatalog)
          setSelectedProduct(null)
        }
      }
    },
    [selectionMode, multiSelect, maxSelections]
  )

  // Transform selection to output format
  const buildSelectionOutput = useCallback((): ProductVariantSelection => {
    const output: ProductVariantSelection = {
      mode: selectionMode,
    }

    if (multiSelect) {
      // Multi-select mode
      if (selectionMode === 'product-only') {
        output.products = search.products
          .filter((p) => selectedProducts.has(p.id))
          .map((p) => ({
            id: p.id,
            name: p.name,
            format: p.format,
            variant_count: p.variant_count,
          }))
      } else if (selectionMode === 'variants-only') {
        output.variants = search.variants
          .filter((v) => selectedVariants.has(v.id))
          .map((v) => ({
            id: v.id,
            name: v.name,
            sku: v.sku,
            gtin: v.gtin,
            format_pack_size: v.format_pack_size,
            product_id: v.product_id,
            product_name: v.product_name,
            distributor_codes: v.distributor_codes,
            avg_price: v.avg_price || null,
          }))
      } else {
        // product-with-variants mode: group selected variants by product
        const productMap = new Map<number, ProductWithSelectedVariants>()

        search.variants
          .filter((v) => selectedVariants.has(v.id))
          .forEach((variant) => {
            const productId = variant.product_id
            if (!productId) return

            if (!productMap.has(productId)) {
              productMap.set(productId, {
                product: {
                  id: productId,
                  name: variant.product_name || 'Unknown Product',
                },
                variants: [],
              })
            }

            productMap.get(productId)!.variants.push({
              id: variant.id,
              name: variant.name,
              sku: variant.sku,
              gtin: variant.gtin,
              format_pack_size: variant.format_pack_size,
              product_id: variant.product_id,
              product_name: variant.product_name,
              distributor_codes: variant.distributor_codes,
              avg_price: variant.avg_price || null,
            })
          })

        output.productsWithVariants = Array.from(productMap.values())
      }
    } else {
      // Single-select mode
      if (selectionMode === 'product-only' && selectedProduct) {
        output.products = [
          {
            id: selectedProduct.id,
            name: selectedProduct.name,
            format: selectedProduct.format,
            variant_count: selectedProduct.variant_count,
          },
        ]
      } else if (selectedVariant) {
        const variantData: SelectedVariant = {
          id: selectedVariant.id,
          name: selectedVariant.name,
          sku: selectedVariant.sku,
          gtin: selectedVariant.gtin,
          format_pack_size: selectedVariant.format_pack_size,
          product_id: selectedVariant.product_id,
          product_name: selectedVariant.product_name,
          distributor_codes: selectedVariant.distributor_codes,
          avg_price: selectedVariant.avg_price || null,
        }

        if (selectionMode === 'variants-only') {
          output.variants = [variantData]
        } else {
          // product-with-variants mode
          output.productsWithVariants = [
            {
              product: {
                id: selectedVariant.product_id!,
                name: selectedVariant.product_name || 'Unknown Product',
              },
              variants: [variantData],
            },
          ]
        }
      }
    }

    return output
  }, [
    selectionMode,
    multiSelect,
    selectedProduct,
    selectedVariant,
    selectedProducts,
    selectedVariants,
    search.products,
    search.variants,
  ])

  // Handle select button click
  const handleSelect = useCallback(() => {
    const selection = buildSelectionOutput()
    onSelect(selection)
    onHide()
  }, [buildSelectionOutput, onSelect, onHide])

  // Helper to check if a row is selected
  const isRowSelected = useCallback(
    (row: Product | VariantCatalog): boolean => {
      if (multiSelect) {
        if (selectionMode === 'product-only') {
          return selectedProducts.has((row as Product).id)
        } else {
          return selectedVariants.has((row as VariantCatalog).id)
        }
      } else {
        if (selectionMode === 'product-only') {
          return selectedProduct?.id === (row as Product).id
        } else {
          return selectedVariant?.id === (row as VariantCatalog).id
        }
      }
    },
    [
      selectionMode,
      multiSelect,
      selectedProduct,
      selectedVariant,
      selectedProducts,
      selectedVariants,
    ]
  )

  // Define columns based on selection mode
  const columns = useMemo<ColumnDef<any, any>[]>(() => {
    if (selectionMode === 'product-only') {
      // Product-only columns
      return [
        productColumnHelper.display({
          id: multiSelect ? 'checkbox' : 'radio',
          header: '',
          cell: ({ row }) => {
            const product = row.original as Product
            const isSelected = isRowSelected(product)
            const isDisabled =
              multiSelect &&
              !isSelected &&
              selectedProducts.size >= maxSelections

            return (
              <div className="flex items-center justify-center">
                <input
                  type={multiSelect ? 'checkbox' : 'radio'}
                  checked={isSelected}
                  disabled={isDisabled}
                  onChange={() => handleRowClick(product)}
                  onClick={(e) => e.stopPropagation()}
                  className="w-4 h-4 cursor-pointer accent-fb-green-800"
                />
              </div>
            )
          },
          size: 50,
          enableSorting: false,
        }),
        productColumnHelper.accessor('name', {
          header: 'Product Name',
          cell: (info) => {
            const value = info.getValue() || '—'
            return (
              <div className="max-w-full">
                <OverflownTextTooltip tooltipText={value}>
                  <div
                    style={{
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {value}
                  </div>
                </OverflownTextTooltip>
              </div>
            )
          },
          size: 250,
          enableSorting: true,
        }),
        productColumnHelper.accessor('variant_count', {
          header: 'Variants',
          cell: (info) => {
            const count = info.getValue() || 0
            return `${count} variant${count !== 1 ? 's' : ''}`
          },
          size: 100,
          enableSorting: true,
        }),
        productColumnHelper.accessor('format', {
          header: 'Format',
          cell: (info) => info.getValue() || '—',
          size: 150,
          enableSorting: true,
        }),
      ]
    } else {
      // Variant columns (for variants-only and product-with-variants modes)
      const variantColumns: ColumnDef<VariantCatalog, any>[] = [
        variantColumnHelper.display({
          id: multiSelect ? 'checkbox' : 'radio',
          header: '',
          cell: ({ row }) => {
            const variant = row.original as VariantCatalog
            const isSelected = isRowSelected(variant)
            const isDisabled =
              multiSelect &&
              !isSelected &&
              selectedVariants.size >= maxSelections

            return (
              <div className="flex items-center justify-center">
                <input
                  type={multiSelect ? 'checkbox' : 'radio'}
                  checked={isSelected}
                  disabled={isDisabled}
                  onChange={() => handleRowClick(variant)}
                  onClick={(e) => e.stopPropagation()}
                  className="w-4 h-4 cursor-pointer accent-fb-green-800"
                />
              </div>
            )
          },
          size: 50,
          enableSorting: false,
        }),
        variantColumnHelper.accessor('name', {
          header: 'Variant Name',
          cell: (info) => {
            const value = info.getValue() || '—'
            return (
              <div className="max-w-full">
                <OverflownTextTooltip tooltipText={value}>
                  <div
                    style={{
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {value}
                  </div>
                </OverflownTextTooltip>
              </div>
            )
          },
          size: 200,
          enableSorting: true,
        }),
      ]

      // Add product column for variants-only mode
      if (
        selectionMode === 'variants-only' ||
        selectionMode === 'product-with-variants'
      ) {
        variantColumns.push(
          variantColumnHelper.accessor('product_name', {
            header: 'Product',
            cell: (info) => {
              const productName = info.getValue()
              const productId = info.row.original.product_id
              const isUnmatched = !productName || !productId

              if (isUnmatched) {
                return (
                  <div className="flex items-center gap-2 text-yellow-600">
                    <TriangleAlert size={16} className="flex-shrink-0" />
                    <span className="text-gray-400">No Product</span>
                  </div>
                )
              }

              return (
                <div className="max-w-full">
                  <OverflownTextTooltip tooltipText={productName}>
                    <div
                      style={{
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {productName}
                    </div>
                  </OverflownTextTooltip>
                </div>
              )
            },
            size: 150,
            enableSorting: true,
          })
        )
      }

      variantColumns.push(
        variantColumnHelper.accessor('sku', {
          header: 'SKU',
          cell: (info) => {
            const value = info.getValue() || '—'
            return (
              <OverflownTextTooltip tooltipText={value}>
                <div style={{ maxWidth: '130px' }}>{value}</div>
              </OverflownTextTooltip>
            )
          },
          size: 130,
          enableSorting: true,
        }),
        variantColumnHelper.display({
          id: 'avg_price_per_unit',
          header: 'Avg Price Per Unit',
          cell: (info) => {
            const variant = info.row.original
            const avgPricePerUnit = variant.avg_price
            if (avgPricePerUnit === null || avgPricePerUnit === undefined) {
              return <span className="text-gray-400">—</span>
            }

            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(avgPricePerUnit)
          },
          size: 160,
          enableSorting: false,
        }),
        variantColumnHelper.accessor('gtin', {
          header: 'GTIN',
          cell: (info) => {
            const value = info.getValue() || '—'
            return (
              <OverflownTextTooltip tooltipText={value}>
                <div style={{ maxWidth: '120px' }}>{value}</div>
              </OverflownTextTooltip>
            )
          },
          size: 150,
          enableSorting: true,
        }),
        variantColumnHelper.accessor('format_pack_size', {
          header: 'Format / Pack Size',
          cell: (info) => {
            const value = info.getValue() || '—'
            return (
              <OverflownTextTooltip tooltipText={value}>
                <div style={{ maxWidth: '150px' }}>{value}</div>
              </OverflownTextTooltip>
            )
          },
          size: 180,
          enableSorting: true,
        })
      )

      // variantColumns.push()

      return variantColumns
    }
  }, [
    selectionMode,
    multiSelect,
    isRowSelected,
    handleRowClick,
    selectedProducts,
    selectedVariants,
    maxSelections,
  ])

  // Get table data based on selection mode and product filter
  // Create options for product filter dropdown
  const productFilterOptions = useMemo(() => {
    return [
      { label: 'All Products', value: '' },
      ...search.products.map((product) => ({
        label: product.name,
        value: product.id.toString(),
      })),
    ]
  }, [search.products])

  const tableData = useMemo(() => {
    if (selectionMode === 'product-only') {
      return search.products
    } else {
      // Filter variants by product if filter is active
      const productId = productFilterValue ? Number(productFilterValue) : null
      if (productId) {
        return search.variants.filter((v) => v.product_id === productId)
      }
      return search.variants
    }
  }, [selectionMode, search.products, search.variants, productFilterValue])

  // Determine loading and error states
  const isLoading =
    selectionMode === 'product-only'
      ? search.productsLoading
      : search.variantsLoading

  const error =
    selectionMode === 'product-only'
      ? search.productsError
      : search.variantsError

  const tableKey = `product-search-modal-${selectionMode}`

  // Read sorting state from DataTable's Zustand store and sync with search hook
  const { sorting } = dataTableSortingStoreRepo.getStore(tableKey)()
  const { setOrdering: setSearchOrdering } = search

  useEffect(() => {
    const ordering = sorting?.[0]
      ? `${sorting[0].desc ? '-' : ''}${sorting[0].id}`
      : undefined
    setSearchOrdering(ordering)
  }, [sorting, setSearchOrdering])

  return (
    <Modal
      open={show}
      onOpenChange={(open) => !open && onHide()}
      title={title}
      size="2xl"
      onAccept={handleSelect}
      acceptButtonText={selectButtonText}
      blockAccept={!hasSelection}
      onCancel={onHide}
      cancelButtonText="Cancel"
      childrenContainerClassName="max-h-[70vh] overflow-y-auto"
    >
      {/* Search Input and Filters Row */}
      <div className="mb-3 flex gap-3">
        <div className="flex-1">
          <input
            type="text"
            className="form-control"
            placeholder="Search by name, SKU, GTIN, or distributor codes..."
            value={search.searchTerm}
            onChange={(e) => search.setSearchTerm(e.target.value)}
          />
        </div>
        {showProductFilter && selectionMode !== 'product-only' && (
          <div className="w-64">
            <Select
              control={control}
              name="productFilter"
              options={productFilterOptions}
              placeholder="All Products"
              className="!mt-0"
            />
          </div>
        )}
      </div>

      <hr className="mt-3 mb-3 border-gray-400" />

      {/* Error State */}
      {error && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-red-800">
          Error loading data: {error.message}
        </div>
      )}

      {/* Table */}
      <div
        className="[&_.selected-row]:!bg-fb-green-50 [&_.clickable-row]:cursor-pointer [&_.clickable-row:hover]:bg-gray-50 [&_.selected-row:hover]:!bg-fb-green-100"
        style={{
          height: '510px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {!error && (
          <DataTableProvider tableKey={tableKey}>
            <ColumnsStoreProvider tableKey={tableKey}>
              <DataTable
                tableKey={tableKey}
                data={tableData}
                columns={columns}
                loading={isLoading}
                enableRowSelection={false}
                sortableFields="all"
                isPaginationEnabled={true}
                height={430}
                autoHideScrollbar={false}
                paginationOptions={{
                  isPaginationLoading: isLoading,
                  setPage: search.setPage,
                  page: search.page,
                  totalRows:
                    selectionMode === 'product-only'
                      ? search.productsCount
                      : search.variantsCount,
                  pageSize: search.pageSize,
                }}
                onRowClick={(row) => handleRowClick(row.original)}
                rowAttributes={(row) => {
                  const isSelected = isRowSelected(row.original)
                  return {
                    className: `clickable-row ${isSelected ? 'selected-row' : ''}`,
                  }
                }}
              />
            </ColumnsStoreProvider>
          </DataTableProvider>
        )}
      </div>

      {/* Selected Product/Variant Badge at Bottom */}
      {hasSelection && (
        <div className="flex justify-center">
          <div
            className="inline-flex items-center rounded-lg gap-2"
            style={{
              padding: '4px 8px',
              background: 'rgba(8, 116, 67, 0.1)',
              color: '#087443',
              fontSize: '14px',
              lineHeight: '24px',
              height: '35px',
            }}
          >
            {multiSelect ? (
              <>
                <span>
                  {selectionMode === 'product-only'
                    ? `${selectedProducts.size} product${selectedProducts.size !== 1 ? 's' : ''} selected`
                    : `${selectedVariants.size} variant${selectedVariants.size !== 1 ? 's' : ''} selected`}
                </span>
                {maxSelections && (
                  <span className="text-xs opacity-75">
                    (
                    {selectionMode === 'product-only'
                      ? selectedProducts.size
                      : selectedVariants.size}{' '}
                    of {maxSelections})
                  </span>
                )}
              </>
            ) : selectionMode === 'product-only' && selectedProduct ? (
              <>
                <span>
                  <b>{selectedProduct.name}</b>
                  {selectedProduct.format && (
                    <span> ({selectedProduct.format})</span>
                  )}
                </span>
              </>
            ) : selectedVariant ? (
              <>
                <span>
                  {selectedVariant.product_name && (
                    <>
                      <b>{selectedVariant.product_name}</b> -{' '}
                    </>
                  )}
                  {selectedVariant.name}
                  {selectedVariant.format_pack_size && (
                    <span> ({selectedVariant.format_pack_size})</span>
                  )}
                </span>
              </>
            ) : null}
          </div>
        </div>
      )}
    </Modal>
  )
}
